import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ChartValue } from '@models/chart-value';
import { JobStatus } from '@models/enums';
import { AggregationStats } from '@models/hierarchy/aggregation-stats';
import { CustomTeam } from '@models/team/custom-team';
import { DisplayJobs } from '@models/team/display-jobs';
import { TeamProfile } from '@models/team/team-profile';
import { UserForTeam } from '@models/team/user-for-team';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  })
};

@Injectable()
export class TeamService {
  teamMeterOwnershipPercent: number;

  constructor(
    private http: HttpClient,
  ) { }

  getTeamList(userId: string, callingProgram?: string): Observable<{ teams: CustomTeam[] }> {
    const id = userId ? userId : '';
    return this.http.get<any>(`/web-app-svc/team/getUserTeams?user=${id}`);
  }

  addTeam(teamObj: CustomTeam): Observable<any> {
    return this.http.post<any>(`/web-app-svc/team/addTeam`, teamObj, httpOptions);
  }

  removeTeam(teamObj: Partial<CustomTeam>): Observable<any> {
    return this.http.post<any>(`/web-app-svc/team/removeTeam`, teamObj, httpOptions);
  }

  editTeamName(teamObj: CustomTeam): Observable<any> {
    return this.http.put<any>(`/web-app-svc/team/updateTeamName?teamId=${teamObj.teamId}&teamName=${teamObj.teamName}`, httpOptions);
  }

  addTeamMember(userInfo: UserForTeam): Observable<any> {
    return this.http.post<any>(`/web-app-svc/team/addTeamMember`, userInfo, httpOptions);
  }

  removeTeamMember(userInfo: UserForTeam): Observable<any> {
    return this.http.post<any>(`/web-app-svc/team/removeTeamMember`, userInfo, httpOptions);
  }

  getUserList(key: string): Observable<any> {
    return this.http.get<any>(`/web-app-svc/team/searchUsers?search=${key}`);
  }

  getTeamProfileByUser(userList: string[], startTime?: number, endTime?: number): Observable<TeamProfile> {
    if (!startTime) {
      const d = new Date();
      startTime = d.setHours(0, 0, 0, 0);
    }
    if (!endTime) {
      const d = new Date();
      endTime = d.setHours(0, 0, 0, 0);
    }

    const endDate = new Date(endTime);
    endDate.setDate(endDate.getDate() + 1);
    endTime = endDate.getTime();
    const requestBody = {
      userList: userList, // should be an array of ids
      since: startTime,
      before: endTime
    };
    const techs = this.http.post<TeamProfile>(`/web-app-svc/team/teamProfileByUser`, requestBody, httpOptions).pipe(
      map(techTeam => {
        return this.generateAggregationStats(
          this.initializeWorkOrders(techTeam)
        );
      })
    );
    return techs;
  }

  getTeamProfile(teamName: string, teamOwner: string, startTime?: number, endTime?: number): Observable<TeamProfile> {
    // default to today starting at 5 , ending at 11:59 pm
    if (!startTime) { const d = new Date(); startTime = d.setHours(0, 0, 0, 0); }
    if (!endTime) { const d = new Date(); endTime = d.setHours(23, 59, 59, 0); }

    const start = startTime.toString();
    const end = endTime.toString();
    const schedule = this.http.get<TeamProfile>('/web-app-svc/team/teamProfile?teamName='
      + teamName + '&owner=' + teamOwner + '&since=' + start + '&before=' + end).pipe(shareReplay(1));

    return schedule;
  }

  // @ts-ignore ALM-277060
  generateAggregationStats(teamProfile) {
    const teamStats: AggregationStats[] = [];
    let meterCount = 0;

    if (teamProfile.members) {
      for (const employee of teamProfile.members) {
        const username = employee.username;
        if (employee.meters && employee.meters.length > 0) {
          meterCount++;
        }

        // calculate job status counts
        const jobMap = new Map<string, number>();
        for (const workOrder of employee.workOrders) {
          if (workOrder.status === 'CP') {
            workOrder.status = 'Complete';
          }

          // @ts-ignore ALM-277060
          if (jobMap[workOrder.status]) {
            // @ts-ignore ALM-277060
            jobMap[workOrder.status] = jobMap[workOrder.status] + 1;
          } else {
            // @ts-ignore ALM-277060
            jobMap[workOrder.status] = 1;
          }
        }

        const jobStatusList: ChartValue[] = [];
        for (const key of Object.keys(jobMap)) {
          // @ts-ignore ALM-277060
          const status: ChartValue = { name: key, value: jobMap[key] };
          jobStatusList.push(status);
        }

        const employeeStats: AggregationStats = {
          username: username,
          employeeCount: 0,
          techCount: 0,
          totalJobs: employee.workOrders.length,
          // @ts-ignore ALM-277060
          completedJobs: jobMap[JobStatus.COMPLETE],
          completedJobsWithMeter: 0,
          meterUsageOnJob: '0',
          jobStatusList: jobStatusList
        };
        teamStats.push(employeeStats);
      }
    }

    // calculate meter ownership percentage
    if (meterCount > 0) {
      this.teamMeterOwnershipPercent = Math.round(
        (100 * meterCount) / teamProfile.members.length
      );
    } else {
      this.teamMeterOwnershipPercent = 0;
    }
    return teamProfile;
  }

  /**
 * Initialize the workOrder objects in team profile with display classes
 * @param teamProfile TeamProfile object
 * @returns no return
 */
  // @ts-ignore ALM-277060
  initializeWorkOrders(teamProfile) {
    // @ts-ignore ALM-277060
    teamProfile.members.forEach(teamMember => {
      // @ts-ignore ALM-277060
      teamMember.workOrders.forEach(wo => {
        wo.displayJobs = this.getDisplayIconsForWorkOrder(wo);
      });
    });
    return teamProfile;
  }

  /**
   * Get the display icon class name to show for each job location (tap, ST, GB, etc)
   * @param workOrder WorkOrder object
   * @returns DisplayJobs object
   */
  // @ts-ignore ALM-277060
  getDisplayIconsForWorkOrder(workOrder): DisplayJobs {
    // initialize all location class names to 'unselected'
    const displayJobs = <DisplayJobs>{
      display: false,
      isTap: 'unselected',
      isGB: 'unselected',
      isHome: 'unselected',
      isST: 'unselected',
      isIngress: 'unselected'
    };

    const mdl = workOrder.meterDataList;
    if (mdl !== null) {
      // @ts-ignore ALM-277060
      mdl.map(m => {
        if (m.dataType === 'INGRESS') {
          displayJobs.isIngress = 'selected';
        } else if (m.dataType === 'SPEED_TEST') {
          displayJobs.isST = 'selected';
        } else if (m.dataType === 'COMPOSITE_DATA') {
          if (m.location.toLowerCase().indexOf('tap') > -1) {
            displayJobs.isTap = 'selected';
          } else if (m.location === 'Ground Block') {
            displayJobs.isGB = 'selected';
          } else {
            displayJobs.isHome = 'selected';
          }
        }
      });
    }
    return displayJobs;
  }
}

